import React, { useState } from 'react';
import VoxelTableCell from '../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableHeadCell from '../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTableRow from '../../../components/voxel/Tables/VoxelTableRow';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { faPaperPlane, faTrash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import VoxelTablePaginationDataSpecific from '../../../components/voxel/Tables/VoxelTablePaginationDataSpecific';
import VoxelTable from '../../../components/voxel/Tables/VoxelTable';
import { PagingInputGq } from '../../../graphql/users/models/PagingInputGq';
import { PagingResultGq } from '../../../graphql/users/models/PagingResultGq';
import {
  UserTeamRoleGq,
  userCanEditMemberWithRole,
} from '../../../graphql/users/models/UserTeamRoleGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { TeamMemberGq } from '../../../graphql/users/models/CurrentTeamUserGq';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';

export interface PendingInvitationsTableProps {
  currentUser: CurrentUserGq;
  users: TeamMemberGq[];
  paging: PagingInputGq;
  pagingResult: PagingResultGq;
  setPaging: (paging: PagingInputGq) => void;
  reload: () => Promise<unknown>;
}

export const PendingInvitationsTable: React.FC<PendingInvitationsTableProps> = ({
  currentUser,
  users,
  paging,
  pagingResult,
  setPaging,
  reload,
}) => {
  const [loadingUserIds, setLoadingUserIds] = useState<number[]>([]);

  // Remove user
  const removeUserAcessMutation = usersApiService.mutations.useRemoveUserAcess();
  const removeUser = (memberId: number) => {
    setLoadingUserIds((prev) => [...prev, memberId]);
    removeUserAcessMutation({ memberId: memberId })
      .catch((errorReason) => console.error(errorReason))
      .then(reload)
      .finally(() => setLoadingUserIds((prev) => prev.filter((uid) => uid != memberId)));
  };

  // Resend invite
  const inviteMutation = usersApiService.mutations.useInviteUser();
  const resendInvitation = (member: TeamMemberGq) => {
    setLoadingUserIds((prev) => [...prev, member.id]);
    inviteMutation({ email: member.email, role: member.teamRole })
      .catch((errorReason) => console.error(errorReason))
      .then(reload)
      .finally(() => setLoadingUserIds((prev) => prev.filter((uid) => uid != member.id)));
  };

  return (
    <>
      <VoxelTable>
        <thead>
          <VoxelTableRow>
            <VoxelTableHeadCell>Email</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">Invited by</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">Invite sent</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">Role</VoxelTableHeadCell>
            <VoxelTableCell />
          </VoxelTableRow>
        </thead>
        <tbody>
          {users.map((member) => (
            <VoxelTableRow
              key={member?.id}
              className={`${loadingUserIds.find((id) => id === member.id) ? 'opacity-40' : ''}`}
            >
              <VoxelTableCell className="py-2.5 select-all">{member?.email}</VoxelTableCell>
              <VoxelTableCell align="center" className="py-2.5">

                <VoxelAvatar
                  name={member?.invitedBy?.email}
                  img={member?.invitedBy?.picture}
                  size='md'
                  tooltip={
                    <>
                      {member?.invitedBy?.givenName + ' ' + member?.invitedBy?.familyName}
                      <br />
                      {member?.invitedBy?.email}
                    </>
                  }
                />

              </VoxelTableCell>
              <VoxelTableCell align="center" className="py-2.5">
                {!member?.invitedTime
                  ? '-'
                  : moment.utc(member?.invitedTime).local().format('MMM Do, YY')}
              </VoxelTableCell>
              <VoxelTableCell align="center" className="py-2.5">
                {member.teamRole.toLowerCase()}
              </VoxelTableCell>
              <VoxelTableCell
                align="right"
                className="flex flex-row gap-1 flex-nowrap items-center justify-end"
              >
                <VoxelIconButton
                  tooltip="Resend invitation"
                  color="white"
                  disabled={
                    moment
                      .duration(moment().diff(moment.utc(member?.invitedTime).local()))
                      .as('weeks') < 1
                  }
                  icon={<FontAwesomeIcon icon={faPaperPlane} size="lg" />}
                  onClick={() => resendInvitation(member)}
                />
                <VoxelIconButton
                  tooltip="Cancel invitation"
                  disabled={
                    (!userCanEditMemberWithRole(currentUser, member.teamRole) &&
                      !(
                        currentUser.teamRole === UserTeamRoleGq.MEMBER &&
                        member.teamRole === UserTeamRoleGq.MEMBER
                      )) ||
                    loadingUserIds.includes(member.id)
                  }
                  onClick={() => removeUser(member.id)}
                  size="xs"
                  color="white"
                  icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
                />
              </VoxelTableCell>
            </VoxelTableRow>
          ))}
          <VoxelTablePaginationDataSpecific
            pagingInput={paging}
            onPagingChange={(event, x) => setPaging(x)}
            pagingResult={pagingResult}
            colSpan={5}
          />
        </tbody>
      </VoxelTable>
    </>
  );
};

export default PendingInvitationsTable;
